import { v4 } from 'uuid';
import { Presentation } from '@visiba/presentation';
import { FileWorkerStale } from '@feature/file_manager/mod';
import { AnalyticService } from '@application/analytic/analytic.service';
import { Injector } from '@visiba/instantiation';
import { FormManager } from '@feature/dynamic_form/form_manager/mod';
import { MessagingCaseModel } from '@feature/cases/case_messaging_model';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import { Logger } from '@visiba/logger';
import type { SocketController } from '@visiba/axon';
import type { ControlledMessage, ControlledSystemMessage, ControlledChatMessage } from './common';
import type { DateService } from '@application/time/date.service';
import type { ChatCommunicatorProtocol, ParsedMessage } from './chat_communicator_protocol';

interface PresentationForBridge {
	hasReceivedHistory: boolean;
	isCompleted: boolean;
	windowHasFocus: boolean;
	messages: ControlledMessage[];
}

export interface ChatCommunicatorMetadata {
	readonly hasReadUntilTimestamp: VisibaDate | null;
	readonly issuesTrackedById?: string;
}

export class ChatCommunicator {
	public readonly presentation = Presentation.create<PresentationForBridge>({
		hasReceivedHistory: false,
		isCompleted: false,
		windowHasFocus: window.document.visibilityState === 'visible' && window.document.hasFocus(),
		messages: [],
	});
	public readonly formManager: FormManager;

	private disposers: (() => void)[] = [];
	private lastReadMessageTimestamp: VisibaDate | null;
	private readonly logger = new Logger('ChatCommunicator');

	constructor(
		private readonly communicatorProtocol: ChatCommunicatorProtocol,
		private readonly issuesTrackedById: string | null,
		private readonly connectionId: string,
		private readonly metaData: ChatCommunicatorMetadata,
		private readonly dateService: DateService,
		private readonly controller: SocketController,
		private readonly analyticService: AnalyticService,
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
	) {
		this.bindEvents();
		this.lastReadMessageTimestamp = this.metaData.hasReadUntilTimestamp;
		this.formManager = this.injector.resolve(FormManager);
	}

	public sendMessage(message: string | null, fileTickets: string[] = []): void {
		this.controller.publish(
			this.communicatorProtocol.topics.sendMessage,
			this.communicatorProtocol.parseOutgoingMessage(message, fileTickets),
		);

		const analyticQuirk_userOnlySentText = message != null && fileTickets.length === 0;
		const analyticQuirk_action = analyticQuirk_userOnlySentText ? 'Message with text sent' : 'Message with attachment sent';

		this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, analyticQuirk_action);

		// Todo: do local diff checks. So we don't need to wait for the messages
		// you've sent on the client.
		// this.updateMessages([
		// 	{
		// 		id: v4(),
		// 		isMe: true,
		// 		fromId: -1,
		// 		hasRendered: false,
		// 		text: message,
		// 	},
		// ]);
	}

	public getAllMessages(): void {
		this.communicatorProtocol.getHistory(this.controller, (response) => {
			if (!this.communicatorProtocol.isValidSession(response)) {
				return;
			}

			const messages = this.communicatorProtocol.parseIncomingMessages(response);
			this.handleRetrieveMessage(messages, true);

			this.presentation.write({
				hasReceivedHistory: true,
			});
		});
	}

	public destroy(): void {
		this.logger.logInformation('Destroyed bind events for chat');
		this.disposers.forEach((disposer) => disposer());
	}

	public setReadUntil(controlledMessage: ControlledChatMessage | ControlledSystemMessage): void {
		if (controlledMessage.hasBeenRead) return;
		// To return to more prioritized work.
		queueMicrotask(() => {
			controlledMessage.hasBeenRead = true;
			const message = this.getUserMessageById(controlledMessage.id);
			const timestamp = message.timestamp.add(250, 'milliseconds');
			if (timestamp.isAfter(this.lastReadMessageTimestamp)) {
				this.lastReadMessageTimestamp = timestamp;

				this.controller.publish('setReadUntil', {
					timestamp: timestamp.toISOString(),
					caseId: this.connectionId,
				});
			}

			if (this.issuesTrackedById != null) {
				this.unhandledIssuesService.removeIssuesByTag(this.issuesTrackedById, 'unreadMessages');
			}
		});
	}

	private bindEvents(): void {
		this.logger.logInformation('Bind events for chat');

		this.controller.onOpen = () => {
			this.logger.logInformation('Chat controller invoked "onOpen"');

			queueMicrotask(() => {
				this.getAllMessages();
			});
		};

		const setWindowVisibilityCallback = (): void => {
			this.presentation.write({ windowHasFocus: window.document.visibilityState === 'visible' && window.document.hasFocus() });
		};

		// Since visibilitychange maintain visibilityState as "visible" even if the browser
		// window is below other windows we need to maintain this state using the focus/blur-events
		// as well. This will ensure that we will only send read-receipts when the user has focused
		// the browser content and the window/tab is visible.
		window.addEventListener('visibilitychange', setWindowVisibilityCallback, false);
		window.addEventListener('focus', setWindowVisibilityCallback, false);
		window.addEventListener('blur', setWindowVisibilityCallback, false);

		this.disposers = [
			() => {
				window.removeEventListener('visibilitychange', setWindowVisibilityCallback, false);
				window.removeEventListener('focus', setWindowVisibilityCallback, false);
				window.removeEventListener('blur', setWindowVisibilityCallback, false);
			},

			this.controller.on(this.communicatorProtocol.topics.retrieveMessages, (response: unknown) => {
				if (!this.communicatorProtocol.isValidSession(response)) return;

				const messages = this.communicatorProtocol.parseIncomingMessages(response);
				const [containedAForm, containedAnAttachment] = this.handleRetrieveMessage(messages);

				const analyticQuirk_receivedMessageFromPeer = messages.some((m) => !m.isSystemMessage && !m.sender.isMe);
				if (analyticQuirk_receivedMessageFromPeer) {
					// TODO: DEV-14218 make category generic/fetch from AnalyticContext
					this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received message from peer');

					if (containedAForm) {
						this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received form from peer');
					}

					if (containedAnAttachment) {
						this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received attachment from peer');
					}
				}
			}),

			this.controller.on('case_closed', (caseId: number) => {
				if (caseId.toString() !== this.connectionId) return;
				this.presentation.write({
					isCompleted: true,
				});
			}),

			// todo fix?
			// this.controller.on('onReconnected', () => {
			// 	this.getAllMessages();
			// });

			this.controller.on('app_error', () => {
				// not implemented
			}),
			this.controller.on('duplicate_connection', () => {
				// not implemented
			}),
		];
	}

	private handleRetrieveMessage(messages: ParsedMessage[], isOld = false): [containedAForm: boolean, containedAnAttachment: boolean] {
		// We need a way to tell how many have written in the chat to determine if
		// we should show the "unread messages" banner.
		const actors = new Set<number>();
		const hasReadUntilTimestamp = this.metaData.hasReadUntilTimestamp;

		const controlledMessage = (message: ParsedMessage): ControlledChatMessage | ControlledSystemMessage => {
			let text = message.text;
			if (text != null) {
				text = text.trim();
			}

			if (message.isSystemMessage && text) {
				actors.add(0);

				return {
					type: 'systemMessage',
					text,
					id: v4(),
					fromId: v4(),
					timestamp: this.dateService.create(message.timestamp),
					hasBeenRead: !hasReadUntilTimestamp?.isBefore(message.timestamp),
				};
			}

			let senderNameWithTitle: string;
			let fromId: number;
			let senderName: string;
			let senderImageUrl: string | null;
			let isMe: boolean;
			let isAutoreplyMessage: boolean;
			let fromInitials: string | null = null;

			if (message.autoReplyReception != null) {
				senderNameWithTitle = message.autoReplyReception.name ?? '';
				fromId = message.autoReplyReception.receptionId;
				senderName = message.autoReplyReception.name || '';
				senderImageUrl = message.autoReplyReception.profileImageUrl;
				isMe = false;
				isAutoreplyMessage = true;
			} else {
				senderNameWithTitle = `${message.sender.title ? message.sender.title + ', ' : ''}${message.sender.name}`;
				fromId = message.sender.id;
				senderName = message.sender.name;
				senderImageUrl = message.sender.profileImageUrl;
				isMe = message.sender.isMe;
				isAutoreplyMessage = false;
				fromInitials = message.sender.fromInitials;
			}

			actors.add(fromId);

			return {
				type: 'chatMessage',
				id: v4(),
				hasRendered: isOld,
				text,
				senderName,
				fromInitials,
				senderNameWithTitle,
				senderImageUrl,
				fromId,
				isMe,
				isAutoreplyMessage,
				timestamp: this.dateService.create(message.timestamp),
				hasBeenRead: !hasReadUntilTimestamp?.isBefore(message.timestamp),
				isRedRobinInitialMessage: message.redRobinSessionId != null,
				staleFileWorkers: message.files.map(
					(file) =>
						new FileWorkerStale({
							downloadUrl: file.url,
							fileName: file.fileName,
							fileSize: file.length,
							fileType: file.type,
							thumbnailUrl: file.url + '/thumbnail',
						}),
				),
				// We need to register the forms that may be contained within a message.
				forms: this.formManager.addRespondants(
					message.responseStatuses.map((respondant) => ({
						...respondant,
						issueTrackingId: MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + this.connectionId,
						dataProvider: {
							type: 'respondant',
							respondantId: respondant.respondantId,
						},
					})),
				),
			};
		};

		let analyticQuirk_didContainAForm = false;
		let analyticQuirk_didContainAnAttachment = false;

		let insertNewUnreadIndex = -1;
		const parsedMessages: ControlledMessage[] = [];
		for (let index = 0; index < messages.length; index++) {
			const rawMessage = messages[index];
			const message = controlledMessage(rawMessage);

			if (!analyticQuirk_didContainAForm) {
				analyticQuirk_didContainAForm = rawMessage.responseStatuses.length > 0;
			}

			if (!analyticQuirk_didContainAnAttachment) {
				analyticQuirk_didContainAnAttachment = rawMessage.files.length > 0;
			}

			parsedMessages.push(message);

			// We already have a new unread message, we don't want the last message to
			// be flagged as the unread one.
			if (insertNewUnreadIndex !== -1) continue;

			if (hasReadUntilTimestamp?.isBefore(message.timestamp)) {
				insertNewUnreadIndex = index;
			}
		}

		// We don't want to show unread if there's only one that have been active in
		// the chat.
		if (actors.size > 1 && insertNewUnreadIndex > 0) {
			parsedMessages.splice(insertNewUnreadIndex, 0, {
				type: 'unreadTimestampMessage',
				id: v4(),
				fromId: v4(),
			});
		}

		if (isOld) {
			this.resetMessages();
		}

		this.updateMessages(parsedMessages);

		return [analyticQuirk_didContainAForm, analyticQuirk_didContainAnAttachment];
	}

	private updateMessages(messages: ControlledMessage[]): void {
		this.presentation.write((prev) => {
			const prevMessages = prev.extract().messages as unknown as ControlledMessage[];

			return {
				messages: [...prevMessages, ...messages],
			};
		});
	}

	private getUserMessageById(id: string): ControlledChatMessage | ControlledSystemMessage {
		const { messages } = this.presentation.read();
		const message = messages.find((message) => message.id === id);

		if (message == null) throw new Error(`Could not find a message with id ${id}`);
		if (message.type !== 'chatMessage' && message.type !== 'systemMessage')
			throw new Error(`The message was found but it was not of type "ControlledChatMessage" but rather: ${message.type}`);

		return message;
	}

	private resetMessages(): void {
		this.presentation.write({
			messages: [],
		});
	}
}
